import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const CreditPurchaseSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"verifying" | "success" | "error">("verifying");
  const [creditsAdded, setCreditsAdded] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const verify = async () => {
      const sessionId = searchParams.get("session_id");
      const purchaseId = searchParams.get("purchase_id");

      if (!sessionId || !purchaseId) {
        setStatus("error");
        setErrorMsg("Missing payment information.");
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke("verify-credit-payment", {
          body: { sessionId, purchaseId },
        });

        if (error) throw error;
        if (data?.success) {
          setCreditsAdded(data.credits_added);
          setStatus("success");
        } else {
          throw new Error(data?.error || "Verification failed");
        }
      } catch (err: any) {
        console.error("Verify error:", err);
        setStatus("error");
        setErrorMsg(err.message || "Could not verify payment.");
      }
    };

    verify();
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full text-center space-y-6">
        {status === "verifying" && (
          <>
            <Loader2 className="h-16 w-16 text-primary mx-auto animate-spin" />
            <h1 className="text-2xl font-bold">Verifying Payment...</h1>
            <p className="text-muted-foreground">Please wait while we confirm your purchase.</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
            <h1 className="text-2xl font-bold">Purchase Complete!</h1>
            <p className="text-muted-foreground">
              {creditsAdded} credit{creditsAdded !== 1 ? "s" : ""} have been added to your account.
            </p>
            <Button onClick={() => navigate(-1)} className="mt-4">
              Continue Creating
            </Button>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="h-16 w-16 text-destructive mx-auto" />
            <h1 className="text-2xl font-bold">Something Went Wrong</h1>
            <p className="text-muted-foreground">{errorMsg}</p>
            <p className="text-xs text-muted-foreground">
              If you were charged, your credits will be added shortly. Contact support if the issue persists.
            </p>
            <Button onClick={() => navigate(-1)} variant="outline" className="mt-4">
              Go Back
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default CreditPurchaseSuccess;
